import React from "react";
import { useGroupCategoryPageManagement } from "../../hooks/useGroupCategoryPageManagement";
import { GroupCategoryPage } from "../../types/post";
import { MultiLanguageCategoryForm } from "./MultiLanguageCategoryForm";

interface GroupCategoryPageManagerProps {
  className?: string;
}

export const GroupCategoryPageManager: React.FC<
  GroupCategoryPageManagerProps
> = ({ className = "" }) => {
  const {
    groupPages,
    unassignedCategories,
    loading,
    showGroupPageForm,
    showGroupPageEditForm,
    editingGroupPage,
    handleCreateGroupPage,
    handleUpdateGroupPage,
    handleEditGroupPage,
    handleDeleteGroupPage,
    handleAssignCategoriesToGroup,
    handleRemoveCategoriesFromGroup,
    openNewGroupPageForm,
    closeGroupPageForm,
    closeGroupPageEditForm,
  } = useGroupCategoryPageManagement();

  if (loading) {
    return (
      <div className={`flex justify-center items-center p-8 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Ładowanie grup kategorii...</span>
      </div>
    );
  }

  const handleAssignCategory = async (
    groupPageId: string,
    categoryId: string
  ) => {
    await handleAssignCategoriesToGroup(groupPageId, [categoryId]);
  };

  const handleRemoveCategory = async (categoryId: string) => {
    await handleRemoveCategoriesFromGroup([categoryId]);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">
          Zarządzanie Grupami Kategorii
        </h2>
        <button
          onClick={openNewGroupPageForm}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Dodaj Grupę Kategorii
        </button>
      </div>

      {/* Unassigned Categories Section */}
      {unassignedCategories.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-yellow-800 mb-3">
            Kategorie bez grupy ({unassignedCategories.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {unassignedCategories.map((category) => (
              <div
                key={category.id}
                className="flex items-center justify-between bg-white p-3 rounded border border-yellow-200"
              >
                <div className="flex items-center space-x-2">
                  {category.icon && (
                    <span className="text-lg">{category.icon}</span>
                  )}
                  <span
                    className="font-medium"
                    style={{ color: category.color }}
                  >
                    {category.name || category.id}
                  </span>
                </div>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAssignCategory(e.target.value, category.id);
                      e.target.value = "";
                    }
                  }}
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                  defaultValue=""
                >
                  <option value="">Przypisz do grupy</option>
                  {groupPages.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name || "Unnamed Group"}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Group Category Pages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {groupPages.map((groupPage) => (
          <GroupCategoryPageCard
            key={groupPage.id}
            groupPage={groupPage}
            onEdit={handleEditGroupPage}
            onDelete={handleDeleteGroupPage}
            onRemoveCategory={handleRemoveCategory}
          />
        ))}
      </div>

      {/* Empty State */}
      {groupPages.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-5xl mb-4">📁</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Brak grup kategorii
          </h3>
          <p className="text-gray-500 mb-4">
            Utwórz pierwszą grupę kategorii, aby organizować swoje kategorie.
          </p>
          <button
            onClick={openNewGroupPageForm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Dodaj Grupę Kategorii
          </button>
        </div>
      )}

      {/* Create Form Modal */}
      {showGroupPageForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Nowa Grupa Kategorii</h3>
            <MultiLanguageCategoryForm
              category={null}
              isOpen={true}
              onClose={closeGroupPageForm}
              onSubmit={async (translations, groupKey, icon, color) => {
                await handleCreateGroupPage(
                  translations,
                  groupKey, // slug
                  icon,
                  color,
                  false, // isPublic
                  0 // displayOrder
                );
              }}
            />
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {showGroupPageEditForm && editingGroupPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              Edytuj Grupę Kategorii
            </h3>
            <MultiLanguageCategoryForm
              category={{
                id: editingGroupPage.id,
                group_key: editingGroupPage.slug,
                icon: editingGroupPage.icon,
                color: editingGroupPage.color,
                name: editingGroupPage.name || "",
                description: editingGroupPage.description || "",
                translations: editingGroupPage.translations,
              }}
              isOpen={true}
              onClose={closeGroupPageEditForm}
              onSubmit={async (translations, groupKey, icon, color) => {
                await handleUpdateGroupPage(
                  editingGroupPage.id,
                  translations,
                  groupKey, // slug
                  icon,
                  color,
                  editingGroupPage.is_public,
                  editingGroupPage.display_order
                );
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface GroupCategoryPageCardProps {
  groupPage: GroupCategoryPage;
  onEdit: (groupPage: GroupCategoryPage) => void;
  onDelete: (id: string) => void;
  onRemoveCategory: (categoryId: string) => void;
}

const GroupCategoryPageCard: React.FC<GroupCategoryPageCardProps> = ({
  groupPage,
  onEdit,
  onDelete,
  onRemoveCategory,
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          {groupPage.icon && <span className="text-xl">{groupPage.icon}</span>}
          <div>
            <h3
              className="font-semibold text-lg"
              style={{ color: groupPage.color }}
            >
              {groupPage.name || "Unnamed Group"}
            </h3>
            {groupPage.slug && (
              <p className="text-sm text-gray-500">/{groupPage.slug}</p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onEdit(groupPage)}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            Edytuj
          </button>
          <button
            onClick={() => onDelete(groupPage.id)}
            className="text-red-600 hover:text-red-800 text-sm"
          >
            Usuń
          </button>
        </div>
      </div>

      {/* Description */}
      {groupPage.description && (
        <p className="text-gray-600 text-sm mb-3">{groupPage.description}</p>
      )}

      {/* Categories */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Kategorie ({groupPage.categories?.length || 0})
        </h4>
        {groupPage.categories && groupPage.categories.length > 0 ? (
          <div className="space-y-2">
            {groupPage.categories.map((category) => (
              <div
                key={category.id}
                className="flex items-center justify-between bg-gray-50 p-2 rounded border"
              >
                <div className="flex items-center space-x-2">
                  {category.icon && (
                    <span className="text-sm">{category.icon}</span>
                  )}
                  <span
                    className="text-sm font-medium"
                    style={{ color: category.color }}
                  >
                    {category.name || category.id}
                  </span>
                  {category.group_key && (
                    <span className="text-xs text-gray-500 bg-gray-200 px-1 rounded">
                      {category.group_key}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => onRemoveCategory(category.id)}
                  className="text-red-600 hover:text-red-800 text-xs"
                  title="Usuń z grupy"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm italic">
            Brak kategorii w tej grupie
          </p>
        )}
      </div>

      {/* Metadata */}
      <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
        <div className="flex justify-between">
          <span>Kolejność: {groupPage.display_order}</span>
          <span>{groupPage.is_public ? "Publiczna" : "Prywatna"}</span>
        </div>
      </div>
    </div>
  );
};
